import React from "react";
import { Link } from "react-router-dom";
import { getAuthToken } from "../lib/auth";

const API_BASE = ((process.env.REACT_APP_API_URL || "https://streamsite-v8dd.onrender.com")
  .trim()
  .replace(/\/$/, "")
  .replace(/\/api$/, ""));

function normalizeIntent(value) {
  const v = String(value || "").trim().toLowerCase().replace(/-/g, "_");
  return v === "create" || v === "create_new" || v === "new" ? "create_new" : "link_existing";
}

function safeReturnUrl(raw) {
  try {
    const url = new URL(String(raw || ""));
    if (url.protocol !== "https:") return null;
    if (url.hostname !== "america.cool" && url.hostname !== "www.america.cool") return null;
    return url;
  } catch {
    return null;
  }
}

export default function AmericaCoolAuthorize() {
  const params = React.useMemo(() => new URLSearchParams(window.location.search || ""), []);
  const handle = String(params.get("handle") || params.get("americaCoolHandle") || "").trim().replace(/^@/, "");
  const requestedChannel = String(params.get("channel") || params.get("gaChannel") || "").trim();
  const returnUrl = React.useMemo(() => safeReturnUrl(params.get("returnTo") || params.get("return_url")), [params]);

  const [intent, setIntent] = React.useState(() => normalizeIntent(params.get("intent") || params.get("mode")));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [canCreate, setCanCreate] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const token = getAuthToken();

  const loginNext = `${window.location.pathname}${window.location.search}`;

  const deliverProof = React.useCallback((data) => {
    const message = {
      type: "ga:america-cool-link-proof",
      ownershipProof: data.ownershipProof,
      expiresIn: data.expiresIn,
      claim: data.claim,
    };

    const targetOrigin = returnUrl?.origin || "https://america.cool";
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(message, targetOrigin);
      setDone(true);
      window.setTimeout(() => {
        try { window.close(); } catch {}
      }, 900);
      return;
    }

    if (returnUrl) {
      const hash = new URLSearchParams({
        gaOwnershipProof: String(data.ownershipProof || ""),
        channel: String(data.claim?.channelSlug || ""),
        handle: String(data.claim?.americaCoolHandle || ""),
        intent: String(data.claim?.intent || ""),
      });
      returnUrl.hash = hash.toString();
      window.location.assign(returnUrl.toString());
      return;
    }

    setDone(true);
  }, [returnUrl]);

  const approve = async () => {
    if (!token || busy) return;
    if (!handle) {
      setError("America.cool did not provide a handle for this authorization.");
      return;
    }

    setBusy(true);
    setError("");
    setCanCreate(false);
    try {
      const response = await fetch(`${API_BASE}/api/integrations/america-cool/link-proof`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          americaCoolHandle: handle,
          gaChannel: requestedChannel || undefined,
          intent,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCanCreate(Boolean(data?.canCreate));
        throw new Error(
          data?.error === "ga_channel_not_owned_by_user"
            ? "That channel name does not belong to the GA account currently signed in."
            : data?.error === "ga_channel_not_found"
              ? "That GA channel does not exist yet. You can explicitly create your own channel instead."
              : data?.error === "ga_channel_already_exists"
                ? "That GA channel already exists. Choose Link existing channel instead."
                : data?.error || "GA could not authorize this channel link."
        );
      }
      deliverProof(data);
    } catch (err) {
      setError(err?.message || "GA could not authorize this channel link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 20, background: "#080a10", color: "#fff" }}>
      <section style={{ width: "min(560px, 100%)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 22, padding: 24, background: "rgba(17,19,28,.96)", boxShadow: "0 24px 80px rgba(0,0,0,.45)" }}>
        <div style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#ff7d8a", fontWeight: 800 }}>America.cool × GAStreaming</div>
        <h1 style={{ margin: "10px 0 8px", fontSize: 30 }}>Approve your GA channel</h1>
        <p style={{ margin: "0 0 18px", color: "rgba(255,255,255,.72)", lineHeight: 1.55 }}>
          Channel names alone cannot claim a GAStreaming channel. You must be signed into the GA account that owns the requested username.
        </p>

        <div style={{ display: "grid", gap: 10, padding: 14, borderRadius: 14, background: "rgba(255,255,255,.05)" }}>
          <div><strong>America.cool handle:</strong> @{handle || "missing"}</div>
          <div><strong>GA account/channel:</strong> {requestedChannel || "your signed-in GA username"}</div>
          <div><strong>Action:</strong> {intent === "create_new" ? "Create my GA channel + Mux playback" : "Link my existing GA channel"}</div>
        </div>

        {!token ? (
          <div style={{ marginTop: 18 }}>
            <p style={{ color: "#ffd5d9" }}>Sign in to GAStreaming to prove this channel belongs to you.</p>
            <Link
              to={`/login?next=${encodeURIComponent(loginNext)}`}
              style={{ display: "inline-flex", padding: "11px 16px", borderRadius: 999, background: "#c92d35", color: "#fff", textDecoration: "none", fontWeight: 800 }}
            >
              Sign in to GAStreaming
            </Link>
          </div>
        ) : done ? (
          <div style={{ marginTop: 18, padding: 14, borderRadius: 14, background: "rgba(62,180,110,.13)", border: "1px solid rgba(62,180,110,.3)" }}>
            Approved. Returning this verified claim to America.cool.
          </div>
        ) : (
          <>
            {error ? <div style={{ marginTop: 18, padding: 12, borderRadius: 12, background: "rgba(201,45,53,.14)", border: "1px solid rgba(201,45,53,.35)" }}>{error}</div> : null}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 18 }}>
              <button
                type="button"
                onClick={approve}
                disabled={busy || !handle}
                style={{ cursor: busy ? "wait" : "pointer", border: 0, borderRadius: 999, padding: "11px 17px", fontWeight: 800, background: "#c92d35", color: "#fff" }}
              >
                {busy ? "Verifying…" : intent === "create_new" ? "Create my GA channel" : "Approve existing channel"}
              </button>

              {canCreate && intent !== "create_new" ? (
                <button
                  type="button"
                  onClick={() => { setIntent("create_new"); setError(""); setCanCreate(false); }}
                  style={{ cursor: "pointer", borderRadius: 999, padding: "11px 17px", fontWeight: 800, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.25)" }}
                >
                  Create a new channel instead
                </button>
              ) : null}

              {intent === "create_new" ? (
                <button
                  type="button"
                  onClick={() => { setIntent("link_existing"); setError(""); setCanCreate(false); }}
                  style={{ cursor: "pointer", borderRadius: 999, padding: "11px 17px", fontWeight: 800, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.25)" }}
                >
                  Link existing instead
                </button>
              ) : null}
            </div>
          </>
        )}

        <p style={{ margin: "18px 0 0", fontSize: 12, color: "rgba(255,255,255,.52)", lineHeight: 1.5 }}>
          GA passwords and Mux stream keys are never sent to America.cool. The approval proof expires after 10 minutes and is bound to this handle, channel, and action.
        </p>
      </section>
    </main>
  );
}
